import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import { DataMessageTypeEnum } from '@terra/shared-dto-chat-types';
import { computeHasPendingHITL } from '@terra/ui-design-system';
import type { CoPilotTraceExportStatus } from './co-pilot-trace-export';

export type CoPilotTraceStatus = CoPilotTraceExportStatus;

export type CoPilotChatInstanceStatus =
  | 'streaming'
  | 'submitted'
  | 'ready'
  | 'error'
  | null;

export function resolveCoPilotTraceStatusFromChatStatus(
  chatStatus: CoPilotChatInstanceStatus,
): CoPilotTraceStatus {
  if (chatStatus === 'streaming' || chatStatus === 'submitted') {
    return 'running';
  }

  if (chatStatus === 'error') {
    return 'error';
  }

  return 'completed';
}

function hasStreamingMessageParts(messages: TerraUIMessage[]): boolean {
  return messages.some((message) =>
    message.parts?.some(
      (part) => part.type === 'text' && part.state === 'streaming',
    ),
  );
}

function getDataPartPayload(
  part: TerraUIMessage['parts'][number],
): { type?: string; toolCallId?: string } | null {
  if (!part.type.startsWith('data-')) {
    return null;
  }

  const dataPart = part as { data?: unknown };
  if (typeof dataPart.data !== 'object' || dataPart.data === null) {
    return null;
  }

  return dataPart.data as { type?: string; toolCallId?: string };
}

function hasIncompleteToolCallsOnLastAssistant(
  messages: TerraUIMessage[],
): boolean {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message.role !== 'assistant') {
      continue;
    }

    const pendingToolCallIds = new Set<string>();
    for (const part of message.parts ?? []) {
      const data = getDataPartPayload(part);
      if (!data) {
        continue;
      }

      if (data.type === DataMessageTypeEnum.ToolCallStart && data.toolCallId) {
        pendingToolCallIds.add(data.toolCallId);
      }

      if (
        (data.type === DataMessageTypeEnum.ToolCallComplete ||
          data.type === DataMessageTypeEnum.ToolCallError) &&
        data.toolCallId
      ) {
        pendingToolCallIds.delete(data.toolCallId);
      }
    }

    return pendingToolCallIds.size > 0;
  }

  return false;
}

export function inferCoPilotSessionActiveFromHistory(
  messages: TerraUIMessage[],
): boolean {
  if (messages.length === 0) {
    return false;
  }

  if (hasStreamingMessageParts(messages)) {
    return true;
  }

  if (computeHasPendingHITL(messages)) {
    return true;
  }

  if (hasIncompleteToolCallsOnLastAssistant(messages)) {
    return true;
  }

  return false;
}

export function resolveCoPilotTraceExecutionState({
  chatStatus,
  history,
}: {
  chatStatus: CoPilotChatInstanceStatus;
  history: TerraUIMessage[];
}): {
  traceStatus: CoPilotTraceStatus;
  isSessionActive: boolean;
} {
  const isSessionActiveFromStore =
    chatStatus === 'streaming' || chatStatus === 'submitted';
  const isSessionActiveFromHistory =
    chatStatus == null &&
    history.length > 0 &&
    inferCoPilotSessionActiveFromHistory(history);
  const isSessionActive =
    isSessionActiveFromStore || isSessionActiveFromHistory;
  const traceStatus =
    chatStatus != null
      ? resolveCoPilotTraceStatusFromChatStatus(chatStatus)
      : isSessionActiveFromHistory
        ? 'running'
        : 'completed';

  return { traceStatus, isSessionActive };
}

export function resolveCoPilotTraceHistoryStatus(
  traceStatus: CoPilotTraceStatus,
): 'RUNNING' | 'COMPLETED' | 'FAILED' | 'ABORTED' {
  switch (traceStatus) {
    case 'running':
      return 'RUNNING';
    case 'error':
      return 'FAILED';
    case 'aborted':
      return 'ABORTED';
    case 'completed':
    default:
      return 'COMPLETED';
  }
}
